<script setup lang="ts">
import { onMounted, ref, watch, onUnmounted, onBeforeUnmount, computed } from 'vue';
import { storeToRefs } from 'pinia';
import VirtualScroller from 'primevue/virtualscroller';
import ActivityLogItem from '@/components/ActivityLog/ActivityLogItem.vue';
import { useActivityLogStoreDashboard, useAuthStore, useOrganizationStore } from '@/store';
import { can, useDateTime, useRequestCancel, useRouting } from '@/composables';
import { ACTIVITY_EVENT_TYPE, PERMISSIONS, REQUEST } from '@/enum';

const dateUtils = useDateTime();
const activityLogDashboardStore = useActivityLogStoreDashboard();
const authStore = useAuthStore();
const organizationStore = useOrganizationStore();
const { organizationsIdsSelected } = storeToRefs(organizationStore);
const filterTypeSelected = ref('');
const dates = ref<Date[] | null>([]);
const { logs, loading } = storeToRefs(activityLogDashboardStore);
const $routing = useRouting();
const searchFilter = ref();
const filtersType = ref([
  {
    name: 'All Activity',
    event_type: '',
  },
  {
    name: 'Import Activity',
    event_type: ACTIVITY_EVENT_TYPE.IMPORT,
  },
  { name: 'RSM', event_type: ACTIVITY_EVENT_TYPE.RSM_FILE_PROCESSED, visible: authStore.currentInstanceIsMasterTenant },
]);

watch(
  () => organizationsIdsSelected.value,
  async () => {
    await activityLogDashboardStore.getLogs(organizationsIdsSelected.value);
  },
);

onMounted(async () => {
  activityLogDashboardStore.restorePaginationState(true);
  defineDefaultDates();
  await init();
});

const organizationAssociated: number[] = computed(() =>
  authStore.organizationAssociated.map((organization) => organization.key),
);

const init = async () => {
  defineDateQueryParams();

  if (organizationsIdsSelected.value.length > 0) {
    await activityLogDashboardStore.getLogs(organizationsIdsSelected.value);
  } else {
    const organizationIdToSee = !can([PERMISSIONS.DASHBOARD_ACTIVITY_INDEX_ALL]) ? organizationAssociated?.value : [];
    await activityLogDashboardStore.getLogs(organizationIdToSee || []);
  }
};

const defineDefaultDates = () => {
  const end = new Date();
  const start = new Date(end.getTime() - 28 * 24 * 60 * 60 * 1000);
  const defaultDates: Date[] = [start, end];

  dates.value = defaultDates;
  activityLogDashboardStore.filters.start_at = dateUtils.formatDateForApi(start);
  activityLogDashboardStore.filters.end_at = dateUtils.formatDateForApi(end);
};

const defineDateQueryParams = () => {
  if (dates.value && dates.value.length > 1) {
    activityLogDashboardStore.filters.start_at = dateUtils.formatDateForApi(dates.value[0]);
    activityLogDashboardStore.filters.end_at = dateUtils.formatDateForApi(dates.value[1]);

    $routing.addQueryParams({
      start_at: activityLogDashboardStore.filters.start_at,
      end_at: activityLogDashboardStore.filters.end_at,
    });
  }
};

const onDateRangeChange = async (event: Date[]) => {
  dates.value = event;
  defineDateQueryParams();
  await init();
};

const onSearch = async (search: string) => {
  const hasSearchTerm = search.length >= 3;

  if (hasSearchTerm) {
    activityLogDashboardStore.filters.search = search;
    await init();
  }
};

const onSearchByEvent = async () => {
  activityLogDashboardStore.logs = [];
  activityLogDashboardStore.filters.event_type = filterTypeSelected.value !== '' ? filterTypeSelected.value : '';
  activityLogDashboardStore.loading = false;
  activityLogDashboardStore.currentPage = 0;
  await init();
};

onBeforeUnmount(() => useRequestCancel().requestSaverAbort(REQUEST.LOGS));

onUnmounted(() => activityLogDashboardStore.$reset());

const onLazyLoadLogs = async (event) => {
  const difference = event.last - event.first;

  if (difference >= 30 && difference <= 35) {
    const organizationIdToSee = !can([PERMISSIONS.DASHBOARD_ACTIVITY_INDEX_ALL]) ? organizationAssociated?.value : [];
    await activityLogDashboardStore.getLogs(organizationIdToSee || []);
  }
};
</script>

<template>
  <Card>
    <template #content>
      <div class="grid py-3">
        <div v-if="can([PERMISSIONS.DASHBOARD_ACTIVITY_DATE_VIEW])" class="p-2">
          <div class="w-full">
            <AppDateRangePicker v-model="dates" :value="dates" @change="onDateRangeChange" />
          </div>
        </div>
        <div v-if="can([PERMISSIONS.DASHBOARD_ACTIVITY_FILTER_EVENT_TYPE_VIEW])" class="p-2">
          <Dropdown
            id="type_log"
            v-model="filterTypeSelected"
            :options="filtersType"
            option-label="name"
            option-value="event_type"
            placeholder="All Activity"
            @change="onSearchByEvent"
          />
        </div>
        <div v-if="can([PERMISSIONS.DASHBOARD_ACTIVITY_DATE_SEARCH])" class="col p-2">
          <IconField icon-position="left">
            <InputIcon>
              <Icon name="search" class="text-lg" />
            </InputIcon>
            <InputText
              v-model="searchFilter"
              v-debounce:600ms="onSearch"
              class="w-full pl-5"
              placeholder="Search"
              :min-length="3"
            />
          </IconField>
        </div>
        <div class="col text-right">
          <Spinner v-if="loading" :size="25" />
        </div>
      </div>
      <div v-if="logs.length > 0" class="overflow-y-auto scrollbar flex-1 px-0">
        <VirtualScroller :items="logs" :item-size="50" :lazy="true" scroll-height="550px" @lazy-load="onLazyLoadLogs">
          <template #item="{ item }">
            <ActivityLogItem
              :key="item.id"
              :access-tenant="item.access_tenant"
              :subject="item.subject"
              :description="item.description"
              :subject-type="item.subject_type"
              :causer="item.causer"
              :impersonated-by="item.impersonated_by"
              :event="item.event"
              :properties="item.properties"
              :custom="item.custom"
              :time-ago="item.updated_at"
              :changes="item.changes"
            />
          </template>
        </VirtualScroller>
      </div>
      <div v-if="!loading && !logs.length" class="bg-white p-3 my-3 border-round-sm text-sm mt-5">
        <p class="text-center">No activity was found.</p>
      </div>
    </template>
  </Card>
</template>

<style scoped lang="scss">
.daterangepicker-container {
  max-width: none !important;
  :deep(.p-inputtext) {
    font-weight: bold;
    padding-left: 30px;
  }
}

:deep(.p-dropdown) {
  .p-dropdown-label {
    font-weight: bold;
    &.p-placeholder {
      color: $grayDark;
    }
  }
}
</style>
